import React, { useMemo, useEffect } from 'react';
import type { TimeRange, WidgetSize } from '../../types/dashboard';
import { fmtMoney } from '../../utils/formatters';
import { getTradePnl, normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';
import { filterTradesForDashboardRange } from '../../lib/trades/dashboardBucketAnalytics';

interface LargestGainLossWidgetProps {
  dailyData?: any[];
  trades?: any[];
  timeRange?: TimeRange;
  size?: WidgetSize;
}

export const LargestGainLossWidget: React.FC<LargestGainLossWidgetProps> = ({
  trades = [],
  timeRange = 'month',
  size = 'mini',
}) => {
  const activeTrades = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return [];
    return filterTradesForDashboardRange(trades as Record<string, any>[], timeRange as any);
  }, [trades, timeRange]);

  // compute largest gain/loss from authoritative closed trades only
  const { largestGain, largestLoss } = useMemo(() => {
    const validClosedPnls = activeTrades
      .filter((t) => normalizeTradeStatus(t) !== 'open')
      .map((t) => getTradePnl(t))
      .filter((pnl) => Number.isFinite(pnl) && pnl !== 0);

    if (validClosedPnls.length === 0) {
      return { largestGain: null as number | null, largestLoss: null as number | null };
    }

    const gains = validClosedPnls.filter((pnl) => pnl > 0);
    const losses = validClosedPnls.filter((pnl) => pnl < 0);

    return {
      largestGain: gains.length > 0 ? Math.max(...gains) : 0,
      largestLoss: losses.length > 0 ? Math.min(...losses) : 0,
    };
  }, [activeTrades]);

  const gain = largestGain ?? 0;
  const loss = largestLoss ?? 0;
  const absLoss = Math.abs(loss);
  const ratio = absLoss > 0 ? gain / absLoss : (gain > 0 ? Infinity : 0);

  const getTextColor = () => {
    if (ratio < 1) return 'text-red-500';
    if (ratio < 1.5) return 'text-yellow-500';
    return 'text-emerald-600';
  };

  const totalRange = gain + absLoss;
  const gainPercentage = totalRange > 0 ? (gain / totalRange) * 100 : 50;

  // size-based scaling and alignment adjustments
  // The ratio number should be `text-xl`, description is 12px (`text-xs`),
  // and the $ amounts under the bar should be `text-lg` per design request.
  const ratioSizeClass = 'text-3xl';
  const progressBarHeight = size === 'mini' ? 'h-2' : 'h-4';
  // reduce top spacing so content sits directly under the widget hairline/header
  // use targeted padding (pt) so top padding is smaller while preserving horizontal/pad bottoms
  // 'regular' size is not supported for this widget; treat any non-mini size as the larger/full layout
  const paddingClass = size === 'mini' ? 'pt-1 px-2 pb-2' : 'pt-3 px-6 pb-6';

  useEffect(() => {
    try { console.debug('[WIDGET MOUNT] LargestGainLossWidget mounted'); } catch {}
  }, []);

  if (largestGain == null && largestLoss == null) {
    return (
      <div className={`h-full w-full flex items-center justify-center ${size === 'mini' ? 'px-2 py-2' : 'px-6 py-6'}`}>
        <div className="text-sm text-zinc-500 dark:text-zinc-400">Import Trades to see the widget</div>
      </div>
    );
  }

  return (
    <div data-widget="LargestGainLossWidget" className={`h-full w-full flex flex-col items-center ${paddingClass}`} style={{ marginTop: '-12px' }}>
      <div className="flex flex-col items-center w-full">
        <div className={`font-bold ${ratioSizeClass} ${getTextColor()} text-center`}>
          {ratio === Infinity ? '∞' : `${ratio.toFixed(2)}x`}
        </div>

        <div className="w-full">
          {size === 'mini' ? (
            <div
              data-testid="largest-gain-loss-mini-stats"
              className="mt-0.5 mb-1 grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-1 px-1"
            >
              <div className="flex min-w-0 flex-col items-start">
                <span className="max-w-full whitespace-nowrap text-base font-semibold leading-none text-emerald-600">{fmtMoney(gain)}</span>
                <span className="mt-1 text-[10px] leading-none text-zinc-400">Largest Gain</span>
              </div>
              <div className="pb-px text-center text-[10px] leading-tight text-zinc-400">Gain / Loss<br />Ratio</div>
              <div className="flex min-w-0 flex-col items-end">
                <span className="max-w-full whitespace-nowrap text-base font-semibold leading-none text-red-600">{fmtMoney(absLoss)}</span>
                <span className="mt-1 text-right text-[10px] leading-none text-zinc-400">Largest Loss</span>
              </div>
            </div>
          ) : (
            <div className="w-full grid grid-cols-3 items-center gap-2 mt-1 mb-1 px-1">
              <div className="text-[12px] text-zinc-400 text-left">Largest Gain</div>
              <div className="text-[12px] text-zinc-400 text-center">Gain / Loss Ratio</div>
              <div className="text-[12px] text-zinc-400 text-right">Largest Loss</div>
            </div>
          )}
          <div className={`relative ${progressBarHeight} rounded-full`}>
            <div className="absolute left-0 top-0 h-full bg-emerald-600 rounded-l-full transition-all" style={{ width: `${gainPercentage}%` }} />
            <div className="absolute right-0 top-0 h-full bg-red-500 rounded-r-full transition-all" style={{ width: `${100 - gainPercentage}%` }} />
          </div>
          {size !== 'mini' && (
            <div className="mt-2 flex justify-between px-1 pb-1 text-lg">
              <span className="text-emerald-600 font-semibold">{fmtMoney(gain)}</span>
              <span className="text-red-600 font-semibold">{fmtMoney(absLoss)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
